import type {
  Orientation as OrientationWT,
  Platform,
  WindowSize,
  BoxShadow as WTBoxShadow,
  ElevationShadow as WTElevationShadow,
} from '@ua/web-thomas/types'
import type {SafeAreaInsets} from '@ua/web-thomas/Views/ViewModifiers'

import type {FixMeAny} from 'airship/types'
import {
  arrayResolver,
  enumResolver,
  literalResolver,
  objectResolver,
  optionalResolver,
  resolveNumber,
  resolveString,
  unionResolver,
} from 'airship/utils/resolvers'
import type {Resolver} from 'airship/utils/resolvers/types'

export type Context = {
  has_scene?: boolean
  has_survey?: boolean
  has_embedded?: boolean
  capabilities?: {
    has_scene?: boolean
    has_survey?: boolean
    has_embedded?: boolean
  }
}

export type Settings = {
  common: Common
  iaa: SettingsIAA
  iae: SettingsIAE
}

export type Common = {
  colors: Color[]
  fonts: {
    sizes: FontSizes
    stacks?: FontStack[]
  }
  options?: {
    backgroundPush?: boolean
    miss_behavior?: MissedBehavior
  }
}

export type SettingsIAA = {
  banner: ViewTypeBanner
  modal: ViewTypeModal
  fullscreen: ViewTypeFullScreen
  html: ViewTypeHTML
}

export type SettingsIAE = {
  typographies: Typography[]
  backgrounds: ViewStyleScene[]
  buttons: ButtonIAE[]
  inputs: InputIAE[]
  dismiss: DismissIAE
  indicators: IndicatorsColor
  background: {
    color: Color['id']
  }
  shade: {
    color: Color['id']
  }
  shadow: {
    colorId: Color['id']
    web: ViewShadow['web']
    ios: ViewShadow['ios']
    android: ViewShadow['android']
  }
  embedded?: {
    items: ViewStyleEmbedded[]
  }
  migration?: {
    ui_last_save?: string // iso date
    iaa_to_axp?: string // iso date | IGNORE
  }
  breakpoints?: Breakpoints
} & KeyValue // we have additional key but we don't handle it

export interface BaseElement {
  id: string
  name: string // limit TBD
}

export type DimensionUnit = {
  value: number // if % 1 to 100
  unit: 'px' | '%'
}

export const resolveDimensionUnit: Resolver<DimensionUnit> = objectResolver({
  value: resolveNumber,
  unit: literalResolver(['px', '%']),
})

export type DimensionValues = DimensionUnit | 'auto'

export const resolveDimensionValues: Resolver<DimensionValues> = unionResolver([
  resolveDimensionUnit,
  literalResolver(['auto']),
])

export type Dimension = {
  default: DimensionValues
  min?: DimensionValues
  max?: DimensionValues
}

export const resolveDimension: Resolver<Dimension> = objectResolver({
  default: resolveDimensionValues,
  max: optionalResolver(resolveDimensionValues),
  min: optionalResolver(resolveDimensionValues),
})

export enum BackgroundPosition {
  Top = 'top',
  Middle = 'middle',
  Bottom = 'bottom',
}

export enum Position {
  TOP_LEFT = 'Top-Left',
  TOP_CENTER = 'Top-Center',
  TOP_RIGHT = 'Top-Right',
  CENTER_LEFT = 'Center-Left',
  CENTER_CENTER = 'Center-Center',
  CENTER_RIGHT = 'Center-Right',
  BOTTOM_LEFT = 'Bottom-Left',
  BOTTOM_CENTER = 'Bottom-Center',
  BOTTOM_RIGHT = 'Bottom-Right',
}

export type ViewStylePosition = BackgroundPosition | Position

export type BackgroundPositionItem = {
  id: BackgroundPosition
  label: string
}

export const BACKGROUND_POSITION_ITEMS: BackgroundPositionItem[] = [
  {id: BackgroundPosition.Top, label: 'Top'},
  {id: BackgroundPosition.Middle, label: 'Middle'},
  {id: BackgroundPosition.Bottom, label: 'Bottom'},
]

export type Item<T> = {
  id: T
  label: string
  disabled?: boolean
  hidden?: boolean
}

export type Orientation = 'portrait' | 'landscape'

export type OrientationSettings = Orientation | 'any'

export const ORIENTATION_ITEMS: Item<OrientationSettings>[] = [
  {id: 'portrait', label: 'Portrait'},
  {id: 'landscape', label: 'Landscape'},
  {id: 'any', label: 'Any'},
]

export enum SelectorSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
  ALL = 'all',
}
export const SELECTOR_SIZE_ITEM: Item<SelectorSize>[] = [
  {id: SelectorSize.SMALL, label: 'Small'},
  {id: SelectorSize.MEDIUM, label: 'Medium'},
  {id: SelectorSize.LARGE, label: 'Large'},
  {id: SelectorSize.ALL, label: 'All'},
]

export interface Selector
  extends Pick<
    ViewStyleScene,
    | 'id'
    | 'height'
    | 'width'
    | 'position'
    | 'borderRadius'
    | 'borderColor'
    | 'borderWidth'
  > {
  size: SelectorSize
  orientation: OrientationSettings
  locked?: boolean
  hideShade?: boolean
  disableShadow?: boolean
}

export interface ViewStyle extends BaseElement {
  height: Dimension
  width: Dimension
  selectors?: Selector[]
}

export interface ViewStyleScene extends ViewStyle {
  position: ViewStylePosition
  locked_orientation?: Orientation | 'none'
  selectors?: Selector[]
  hide_shade?: boolean
  disable_shadow?: boolean
  borderRadius: number
  borderColor?: Color['id']
  borderWidth?: number
}

export interface ViewStyleEmbedded extends ViewStyle {
  embeddedId: string // id define by client in our app
  description?: string
  borderRadius: number
}

export type ViewOptionList = {
  id: string
  title: string
  description: string
}

export interface Typography extends BaseElement {
  font: string
  size: number
  alignment: TextAlignment
  style: FontStyle
  color: Color['id']
  isHeading: boolean
}

export type IAEBorder = {
  radius: number
  size: number
  color: Color['id']
}

export interface ButtonIAE extends BaseElement {
  typography: Typography['id']
  color: Color['id']
  border: IAEBorder
}

export interface InputIAE extends BaseElement {
  typography: Typography['id']
  color: Color['id']
  placeholder: Color['id']
  border: IAEBorder
}

export interface DismissIAE {
  color: Color['id']
}

export type OpacityType = {
  lightmode: number
  darkmode: number
}

export const resolveOpacity: Resolver<OpacityType> = objectResolver({
  lightmode: resolveNumber,
  darkmode: resolveNumber,
})

export type IndicatorsColorElement = {color: Color['id']; opacity?: OpacityType}

export const resolveIndicatorsColorElement: Resolver<IndicatorsColorElement> =
  objectResolver({
    color: resolveString,
    opacity: resolveOpacity,
  })

export interface IndicatorsColor {
  active: IndicatorsColorElement
  inactive: IndicatorsColorElement
}

export const resolveIndicatorsColor: Resolver<IndicatorsColor> = objectResolver(
  {
    active: resolveIndicatorsColorElement,
    inactive: resolveIndicatorsColorElement,
  }
)
export enum Tabs {
  COLORS = 'colors',
  DESIGN = 'design',
  OPTIONS = 'options',
  FONTS = 'fonts',
}

export enum AXPTabs {
  BACKGROUND = 'axp_windows_format',
  TEXT = 'axp_text',
  BUTTONS = 'axp_buttons',
  INPUTS = 'axp_inputs',
}

export enum ViewStylesTabs {
  COLORS = 'viewstyles_colors',
  VIEWSTYLE = 'viewstyles_scenes',
  EMBEDDED = 'viewstyles_embedded',
}

export type ColorPageContext = {
  defaults?: {
    colors?: Color[]
  }
  zoltar_style_defaults?: {
    // reports page has defaults in zoltar_style_defaults
    colors?: Color[]
  }
}

export type InputsPageContext = {
  defaults?: {
    axp?: {
      inputs?: InputIAE[]
    }
  }
}

export type BackgroundPageContext = {
  defaults?: {
    axp?: {
      backgrounds?: ViewStyleScene[]
      embedded?: {items: ViewStyleEmbedded[]}
    }
  }
}

export type AxpStylesContext = {
  axp: SettingsIAE
  colors: Color[]
  font_stacks: FontStack[]
}

export type AxpContextDefaults = {
  defaults?: AxpStylesContext
}

export type ZoltarSharedStylesContext = IAASettingsFields & {
  axp: SettingsIAE
}

export interface Color {
  id: string // uuid
  name: string
  lightmode: string // color hex ex: #FFFFFF
  lightmode_opacity?: number // 0 of 100
  darkmode?: string // color hex ex: #FFFFFF
  darkmode_opacity?: number // 0 of 100
}

export const resolveColor: Resolver<Color> = objectResolver({
  id: resolveString,
  name: resolveString,
  lightmode: resolveString,
  lightmode_opacity: optionalResolver(resolveNumber),
  darkmode: optionalResolver(resolveString),
  darkmode_opacity: optionalResolver(resolveNumber),
})

type KeyValue = {[key: string]: FixMeAny}

export enum ViewType {
  BANNER = 'banner',
  MODAL = 'modal',
  FULLSCREEN = 'fullscreen',
  HTML = 'html',
  EMBEDDED = 'embedded',
}

export enum DarkModeType {
  LIGHTMODE = 'lightmode',
  DARKMODE = 'darkmode',
}
export interface DisplayDimensions {
  height: number
  width: number
}

export interface ScaleDimension {
  height: number // between 0 - 1
  width: number // between 0 - 1
}

export type DeviceType = 'web' | 'ios' | 'android'

export interface DeviceFrame extends DisplayDimensions {
  id: string
  displayName: string
  device: DeviceType
  orientation: OrientationWT
  windowSize: WindowSize
  safeArea: SafeAreaInsets // from web-thomas types
  frameRadius: number
}

export const resolveSafeArea: Resolver<SafeAreaInsets> = objectResolver({
  top: resolveNumber,
  bottom: resolveNumber,
  left: resolveNumber,
  right: resolveNumber,
})

export const resolveDeviceFrame: Resolver<DeviceFrame> = objectResolver({
  id: resolveString,
  displayName: resolveString,
  device: resolveString as Resolver<DeviceType>,
  orientation: resolveString as Resolver<OrientationWT>,
  height: resolveNumber,
  width: resolveNumber,
  safeArea: resolveSafeArea,
  frameRadius: resolveNumber,
  windowSize: literalResolver(['small', 'medium', 'large']),
})

export type FrameDetail = {
  windowSize: WindowSize
  safeArea: SafeAreaInsets // from web-thomas types
  frameRadius: number
}

export type OrientationFrame = DisplayDimensions & FrameDetail

export type DeviceFrameDetail = {
  id: string
  displayName: string
  platform: Platform
  portrait: OrientationFrame
  landscape: OrientationFrame
}

export type DeviceBreakpoints = {
  id: string
  portrait: WindowSize
  landscape: WindowSize
}

export type ViewTypeItem = {id: ViewType; label: string}

export enum Placement {
  TOP = 'top',
  BOTTOM = 'bottom',
}

export type PlacementItem = {id: Placement; label: string}

export enum TextSection {
  HEADING = 'heading',
  BODY = 'body',
  FOOTER = 'footer',
}

export enum Size {
  LARGE = 'large',
  MEDIUM = 'medium',
  SMALL = 'small',
}

export type SizeItem = Item<Size>

export interface FontStyle {
  italic: boolean
  bold: boolean
  underlined: boolean
  [key: string]: boolean
}

export interface BackgroundFields {
  background_color: string
  dismiss_action_color: string
  placement: Placement
  border_radius: number
  allow_fullscreen_display?: boolean
}

export type BackgroundModalHTML = Omit<BackgroundFields, 'placement'>

export type BackgroundFullScreen = Omit<
  BackgroundFields,
  'palcement' | 'border_radius' | 'allow_fullscreen_display'
>

export type BackgroundBanner = Omit<
  BackgroundFields,
  'allow_fullscreen_display'
>

export const ALIGNMENTS = ['left', 'center', 'right'] as const

export type TextAlignment = (typeof ALIGNMENTS)[number]

export interface Heading {
  heading_text_color: string
  heading_size: string
  heading_alignment: TextAlignment
  heading_style: FontStyle
  heading_font_family: string
}
export interface Body {
  body_text_color: string
  body_size: string
  body_alignment: TextAlignment
  body_style: FontStyle
  body_font_family: string
}

export interface Footer {
  footer_text_color: string
  footer_size: string
  footer_alignment: TextAlignment
  footer_style: FontStyle
  footer_font_family: string
}

export type TextFields = Heading & Body & Footer

export type Button = {
  background_color: string
  border_color: string
  border_radius: number
  label_size: Size
  text?: string
  text_color: string
  font_family: string
}

export interface Buttons {
  buttons: Button[]
}

export type ViewTypeBanner = BackgroundBanner &
  Heading &
  Body &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeModal = BackgroundModalHTML &
  Heading &
  Body &
  Footer &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeFullScreen = BackgroundFullScreen &
  Heading &
  Body &
  Footer &
  Buttons &
  KeyValue // we have additional key but we don't handle it

export type ViewTypeHTML = BackgroundModalHTML & KeyValue

export enum FallthroughType {
  SERIF = 'serif',
  SANS_SERIF = 'sans-serif',
}

export interface FontStackUrlData {
  url: string
  mimeType: string
}

export const resolveFontStackUrlData: Resolver<FontStackUrlData> =
  objectResolver({
    url: resolveString,
    mimeType: resolveString,
  })

export interface FontStack {
  fallthrough: FallthroughType
  fonts: string[]
  name: string
  urlData?: FontStackUrlData
}

export const resolveFontStack: Resolver<FontStack> = objectResolver({
  name: resolveString,
  fallthrough: enumResolver(FallthroughType),
  fonts: arrayResolver(resolveString),
  urlData: optionalResolver(resolveFontStackUrlData),
})

export interface Font {
  id: string
  label: string
}

export enum FontSizeKey {
  HEADING = 'heading',
  BODY = 'body',
  FOOTER = 'footer',
  BUTTON = 'button',
}

export enum LabelSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

export type FontSize = {
  [K in LabelSize]: number
}

export type FontSizes = {
  [K in FontSizeKey]: FontSize
}

export type IaaSettingsField =
  | ViewTypeBanner
  | ViewTypeModal
  | ViewTypeFullScreen
  | ViewTypeHTML

export type IAASettingsFields = {
  banner: ViewTypeBanner
  modal: ViewTypeModal
  fullscreen: ViewTypeFullScreen
  html: ViewTypeHTML
  font_sizes: FontSizes
  font_stacks?: FontStack[]
  audience?: {miss_behavior: MissedBehavior}
  axp?: {
    typographies: Typography[]
    buttons: ButtonIAE[]
    inputs: InputIAE[]
  } & KeyValue
  colors?: Color[]
} & KeyValue // we have additional key but we don't handle it

export type ButtonViewType =
  | ViewType.BANNER
  | ViewType.FULLSCREEN
  | ViewType.MODAL

export type PageContext = {
  capabilities?: {
    has_dark_mode?: boolean
  }
  has_dark_mode?: boolean
}

export enum AxpSettingsViewTemplateId {
  SettingsSceneContent = 'settings_scene_template',
  SettingsModalTemplate = 'settings_modal_template',
  SettingsFullscreenTemplate = 'settings_fullscreen_template',
  SettingsBannerTemplate = 'settings_banner_template',
  DarkmodeReleaseAnnouncementSettingsTemplate = 'darkmode_release_announcement_settings_template',
  DarkmodeReleaseAnnouncementComposerTemplate = 'darkmode_release_announcement_composer_template',
}

export interface AxpSettings {
  colors: Color[]
  settings: SettingsIAE
  backgroundPush: boolean
  fontStacks?: FontStack[]
}

export interface ZoltarSettingsData extends AxpSettings {
  // this url is used to direct the saved data
  url: string
  // right now custom fonts are shared between IAA and AXP
  iaaSettings: IAASettingsFields
}

export interface ZoltarSettingsDataResponse {
  success: boolean
}

export interface AxpSettingsContext {
  in_app_default_fields: IAASettingsFields
  background_push: {
    url: string
  }
  web_enabled: boolean
  has_scene: boolean
  has_embedded: boolean
  has_web_scene: boolean
  app_experience_settings: {
    url: string
    colors: Color[]
    settings: SettingsIAE
    font_stacks: FontStack[]
  }
  zoltar_background_push_enabled: boolean
}

export type SaveStatus = 'saving' | 'failed' | 'done'

export enum MissedBehavior {
  Cancel = 'cancel',
  Skip = 'skip',
  Penalize = 'penalize',
}

export type NullableMissedBehavior = MissedBehavior | null

export type MissedBehaviorResponse = {
  miss_behavior: NullableMissedBehavior
}

export enum BackgroundPushStatus {
  Enabled = 'enabled',
  Disabled = 'disabled',
}

export type BackgroundPushStatusResponse = {
  background_push_enabled: boolean
}

export type Breakpoints = {
  large: number
  medium: number
}

export type BoxShadow = Omit<WTBoxShadow, 'color'>

export const resolveBoxShadow: Resolver<BoxShadow> = objectResolver({
  radius: resolveNumber,
  blur_radius: resolveNumber,
  offset_y: optionalResolver(resolveNumber),
  offset_x: optionalResolver(resolveNumber),
})

export type ElevationShadow = Omit<WTElevationShadow, 'color'>

export const resolveElevationShadow: Resolver<ElevationShadow> = objectResolver(
  {
    elevation: resolveNumber,
  }
)

export type ViewShadow = {
  color: Color
  ios: BoxShadow
  web: BoxShadow
  android: ElevationShadow
}

export const resolveViewShadow: Resolver<ViewShadow> = objectResolver({
  color: resolveColor,
  ios: resolveBoxShadow,
  web: resolveBoxShadow,
  android: resolveElevationShadow,
})
